import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { PageLoadingState } from '@/components/kit/PageLoadingState';
import { AppShell } from '@/components/layout/AppShell';
import { useAuth } from '@/context/AuthContext';

const BriefPage = lazy(() => import('@/pages/BriefPage').then((m) => ({ default: m.BriefPage })));
const BudgetPage = lazy(() => import('@/pages/BudgetPage').then((m) => ({ default: m.BudgetPage })));
const ContractPage = lazy(() => import('@/pages/ContractPage').then((m) => ({ default: m.ContractPage })));
const CorrespondencePage = lazy(() =>
  import('@/pages/CorrespondencePage').then((m) => ({ default: m.CorrespondencePage }))
);
const DdrPage = lazy(() => import('@/pages/DdrPage').then((m) => ({ default: m.DdrPage })));
const DrawingsPage = lazy(() => import('@/pages/DrawingsPage').then((m) => ({ default: m.DrawingsPage })));
const AcceptanceTestsPage = lazy(() =>
  import('@/pages/AcceptanceTestsPage').then((m) => ({ default: m.AcceptanceTestsPage }))
);
const FoundationSubdomainLayout = lazy(() =>
  import('@/pages/FoundationSubdomainLayout').then((m) => ({ default: m.FoundationSubdomainLayout }))
);
const GanttPage = lazy(() => import('@/pages/GanttPage').then((m) => ({ default: m.GanttPage })));
const HandoverPage = lazy(() => import('@/pages/HandoverPage').then((m) => ({ default: m.HandoverPage })));
const LoginPage = lazy(() => import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const MilestonesPage = lazy(() => import('@/pages/MilestonesPage').then((m) => ({ default: m.MilestonesPage })));
const MinutesPage = lazy(() => import('@/pages/MinutesPage').then((m) => ({ default: m.MinutesPage })));
const OsiPage = lazy(() => import('@/pages/OsiPage').then((m) => ({ default: m.OsiPage })));
const OverviewPage = lazy(() => import('@/pages/OverviewPage').then((m) => ({ default: m.OverviewPage })));
const PaymentsPage = lazy(() => import('@/pages/PaymentsPage').then((m) => ({ default: m.PaymentsPage })));
const PositionsPage = lazy(() => import('@/pages/PositionsPage').then((m) => ({ default: m.PositionsPage })));
const QualityPage = lazy(() => import('@/pages/QualityPage').then((m) => ({ default: m.QualityPage })));
const RegulatoryPage = lazy(() =>
  import('@/pages/RegulatoryPage').then((m) => ({ default: m.RegulatoryPage }))
);
const RecordsCommsSubdomainLayout = lazy(() =>
  import('@/pages/RecordsCommsSubdomainLayout').then((m) => ({ default: m.RecordsCommsSubdomainLayout }))
);
const ReportsPage = lazy(() => import('@/pages/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const SchedulePage = lazy(() => import('@/pages/SchedulePage').then((m) => ({ default: m.SchedulePage })));
const ScheduleSubdomainLayout = lazy(() =>
  import('@/pages/ScheduleSubdomainLayout').then((m) => ({ default: m.ScheduleSubdomainLayout }))
);
const SetupPage = lazy(() => import('@/pages/SetupPage').then((m) => ({ default: m.SetupPage })));
const ListsSettingsPage = lazy(() =>
  import('@/pages/ListsSettingsPage').then((m) => ({ default: m.ListsSettingsPage }))
);
const SpineNodePage = lazy(() => import('@/pages/SpineNodePage').then((m) => ({ default: m.SpineNodePage })));
const SpinePage = lazy(() => import('@/pages/SpinePage').then((m) => ({ default: m.SpinePage })));
const StakeholdersPage = lazy(() =>
  import('@/pages/StakeholdersPage').then((m) => ({ default: m.StakeholdersPage }))
);
const TrackerPage = lazy(() => import('@/pages/TrackerPage').then((m) => ({ default: m.TrackerPage })));
const VariationsPage = lazy(() =>
  import('@/pages/VariationsPage').then((m) => ({ default: m.VariationsPage }))
);
const VisitsPage = lazy(() => import('@/pages/VisitsPage').then((m) => ({ default: m.VisitsPage })));
const WarrantyPage = lazy(() => import('@/pages/WarrantyPage').then((m) => ({ default: m.WarrantyPage })));

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth();
  if (loading) {
    return <PageLoadingState variant="detail" />;
  }
  if (!session) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Suspense fallback={<PageLoadingState />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route path="/" element={<OverviewPage />} />
          <Route path="/tracker" element={<TrackerPage />} />
          <Route path="/setup" element={<SetupPage />} />
          <Route path="/settings/lists" element={<ListsSettingsPage />} />
          <Route path="/schedule" element={<ScheduleSubdomainLayout />}>
            <Route index element={<SchedulePage />} />
            <Route path="gantt" element={<GanttPage />} />
            <Route path="milestones" element={<MilestonesPage />} />
          </Route>
          <Route path="/gantt" element={<Navigate to="/schedule/gantt" replace />} />
          <Route path="/foundation" element={<FoundationSubdomainLayout />}>
            <Route index element={<StakeholdersPage />} />
            <Route path="contract" element={<ContractPage />} />
            <Route path="positions" element={<PositionsPage />} />
          </Route>
          <Route path="/records" element={<RecordsCommsSubdomainLayout />}>
            <Route index element={<MinutesPage />} />
            <Route path="correspondence" element={<CorrespondencePage />} />
          </Route>
          <Route path="/spine" element={<SpinePage />} />
          <Route path="/spine/:equipmentId" element={<SpineNodePage />} />
          <Route path="/drawings" element={<DrawingsPage />} />
          <Route path="/tests" element={<AcceptanceTestsPage />} />
          <Route path="/ddr" element={<DdrPage />} />
          <Route path="/regulatory" element={<RegulatoryPage />} />
          <Route path="/milestones" element={<Navigate to="/schedule/milestones" replace />} />
          <Route path="/minutes" element={<Navigate to="/records" replace />} />
          <Route path="/payments" element={<PaymentsPage />} />
          <Route path="/positions" element={<Navigate to="/foundation/positions" replace />} />
          <Route path="/variations" element={<VariationsPage />} />
          <Route path="/budget" element={<BudgetPage />} />
          <Route path="/contract" element={<Navigate to="/foundation/contract" replace />} />
          <Route path="/correspondence" element={<Navigate to="/records/correspondence" replace />} />
          <Route path="/osi" element={<OsiPage />} />
          <Route path="/warranty" element={<WarrantyPage />} />
          <Route path="/quality" element={<QualityPage />} />
          <Route path="/visits" element={<VisitsPage />} />
          <Route path="/brief" element={<BriefPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/handover" element={<HandoverPage />} />
          <Route path="/stakeholders" element={<Navigate to="/foundation" replace />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
